/* @flow */

import type { CmsPage as CmsPageType } from "shop-state/types";

import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Wrapper } from "@crossroads/ui-components";
import Breadcrumbs from "components/Breadcrumbs";
import { useGetCmsMeta } from "helpers/get-meta";
import cn from "classnames";

type Props = {
  cmsPage: CmsPageType,
  showBreadcrumbs?: boolean,
};

const toggleAccordionTitle = (e: MouseEvent) => {
  const current = e.currentTarget;

  if (current instanceof HTMLElement) {
    const button: HTMLElement = current;
    const accordion = button.closest(".cms__accordion");

    if (!accordion) {
      return;
    }

    const isOpen = accordion.classList.toggle("open");

    button.setAttribute("aria-expanded", String(isOpen));
  }
};

const convertToButton = (e: HTMLElement, bodyId: string): HTMLButtonElement => {
  const button = document.createElement("button");
  button.innerHTML = e.innerHTML;

  for (const { name, value } of Array.from(e.attributes)) {
    button.setAttribute(name, value);
  }

  if (!button.hasAttribute("aria-expanded")) {
    button.setAttribute("aria-expanded", "false");
  }

  if (!button.hasAttribute("aria-controls")) {
    button.setAttribute("aria-controls", bodyId);
  }

  if (!button.hasAttribute("role") || button.getAttribute("role") !== "button") {
    button.setAttribute("role", "button");
  }

  if (!button.classList.contains("cms__accordionButton")) {
    button.classList.add("cms__accordionButton");
  }

  e.replaceWith(button);

  return button;
};

const CmsPage = ({ cmsPage, showBreadcrumbs = true }: Props): React$Node => {
  const meta = useGetCmsMeta(cmsPage);
  const root = useRef<?HTMLDivElement>();

  useEffect(() => {
    const { current } = root;

    if (current) {
      // Bind accordions here
      const accordions = current.querySelectorAll(".cms__accordion");

      accordions.forEach((e, i) => {
        if (e) {
          const title = e.querySelector(".cms__accordionTitle");
          const body = e.querySelector(".cms__accordionBody");

          if (title && body && title.tagName.toLowerCase() !== "button") {
            body.id = `accordion-body-${String(i)}`;
            const button = convertToButton(title, body.id);

            button.addEventListener("click", toggleAccordionTitle);
          }
          else if (title) {
            title.addEventListener("click", toggleAccordionTitle);
          }
        }
      });
    }

    // Un-bind accordions
    return () => {
      if (current) {
        const accordions = current.querySelectorAll(".cms__accordion");
        for (const e of accordions) {
          e.removeEventListener("click", toggleAccordionTitle);
        }
      }
    };
  }, [root]);

  return (
    <Wrapper>
      <Helmet
        title={meta.title}
        meta={meta.data}
        link={meta.link}
      />

      {showBreadcrumbs &&
        <Breadcrumbs current={cmsPage.contentHeading} />
      }

      {cmsPage.contentHeading ?
        <h1 className="h2">{cmsPage.contentHeading}</h1> :
        null
      }

      <div key={cmsPage.url} ref={root} dangerouslySetInnerHTML={{ __html: cmsPage.content }} className={cn("typography")} />
    </Wrapper>
  );
};

export default CmsPage;
